import { ColumnDef } from '@tanstack/react-table'
import type { Report } from 'api/generated'
import { Button } from 'components/ui/button'
import { Check, Edit2, Trash2 } from 'lucide-react'
import moment from 'moment'

type Reportable = NonNullable<Report['reportable']>

export interface ReportsColumnsProps {
  onUpdateReport: (id: number, admin_notes: string) => void
  onDeleteTarget: (id: number) => void
}

const reportableType = (type: Reportable['type']) => {
  switch (type) {
    case 'post':
      return 'post'
    case 'comment':
      return 'commento'
    case 'user':
      return 'user'
    default:
      return ''
  }
}

const reportableContent = (reportable: Reportable) => {
  switch (reportable.type) {
    case 'post':
    case 'user':
      return <a href={reportable.url}>{reportable.url}</a>
    case 'comment':
      return reportable.content
  }
}

const target = (reportable: Reportable) => (
  <>
    {reportableType(reportable.type)} - {reportableContent(reportable)}
  </>
)

export const reportsColumns = (
  props: ReportsColumnsProps
): ColumnDef<Report>[] => [
  {
    accessorKey: 'created_at',
    header: 'Data richiesta',
    cell: ({ row }) =>
      moment.unix(row.original.created_at).format('DD MMM - HH:mm'),
  },
  {
    accessorKey: 'id',
    header: 'ID',
    cell: ({ getValue }) => (
      <div className='text-center'>{String(getValue())}</div>
    ),
  },

  {
    accessorFn: (row) => `${row.author.id ?? '-'} - ${row.author.name ?? '-'}`,
    header: 'Autore',
  },
  {
    accessorKey: 'notes',
    header: 'Messaggio',
  },
  {
    accessorKey: 'reportable',
    header: 'Target',
    cell: ({ row }) => {
      const { reportable } = row.original
      return reportable ? target(reportable) : ''
    },
  },
  {
    accessorKey: 'admin_notes',
    header: 'Note Admin',
  },
  {
    accessorKey: 'status',
    header: 'Azioni',
    cell: ({ row }) => {
      const { id, status } = row.original

      const handleUpdate = (notes: string) => {
        props.onUpdateReport(id, notes)
      }

      const handleDelete = () => {
        props.onDeleteTarget(id)
      }

      const solveButton = () => (
        <Button
          size='sm'
          onClick={() => {
            const notes = prompt("Inserisci le note dell'admin")
            if (notes && notes !== '') {
              handleUpdate(notes)
            }
          }}
          variant='outline'
        >
          <Check className='mr-1' />
          Solve
        </Button>
      )

      const editButton = () => (
        <Button
          size='sm'
          onClick={() => {
            const notes = prompt("Modifica le note dell'admin")
            if (notes && notes !== '') {
              handleUpdate(notes)
            }
          }}
        >
          <Edit2 className='mr-1' />
          Edit
        </Button>
      )

      const deleteButton = () => (
        <Button
          size='sm'
          variant='destructive'
          onClick={() => {
            if (
              window.confirm('Sei sicuro di voler eliminare questo target?')
            ) {
              handleDelete()
            }
          }}
        >
          <Trash2 className='mr-1' />
          Delete
        </Button>
      )

      return (
        <div className='flex items-center gap-2'>
          {status === 'pending' ? solveButton() : editButton()}
          {row.original.reportable && deleteButton()}
        </div>
      )
    },
  },
]
